import {
  bestsOf,
  describeSet,
  EMPTY_BESTS,
  markPrs,
  PR_NAMES,
  prsFor,
  ExerciseList,
  LogPack,
  planProgress,
  planText,
  prefill,
  SplitList,
  suggestExercises,
  todayIn,
  WorkoutDetail,
  type Exercise,
  type ExerciseHistory,
  type MuscleGroup,
  type PrKind,
  type SetRecord,
  type PlannedExercise,
  type User,
  type WorkoutRecord,
} from '@squats/shared';
import { useLiveQuery } from 'dexie-react-hooks';
import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'wouter';
import { api, errorText, isOffline } from '../api.ts';
import { useUser } from '../auth.tsx';
import { cachedApi, useCachedApi } from '../cache.ts';
import { isPlanner, usePlannedDays } from '../calendar.tsx';
import { ExercisePicker } from '../components/ExercisePicker.tsx';
import { PrBadge } from '../components/PrBadge.tsx';
import { usePeople } from '../components/People.tsx';
import { PlanLine } from '../components/Plan.tsx';
import { SetEntry } from '../components/SetEntry.tsx';
import { Sheet } from '../components/Sheet.tsx';
import { formatDayLong, formatMonthDay } from '../dates.ts';
import { db, type LocalSet, type LocalWorkout } from '../log/db.ts';
import * as store from '../log/store.ts';
import { mergeFromServer, packPath } from '../log/sync.ts';

const timeFormat = new Intl.DateTimeFormat(undefined, { timeStyle: 'short' });

/**
 * A workout: your own is edited on this device (and works offline), and so is anyone's for the planner (who logs
 * workouts done together); otherwise someone else's is shown read-only from the server. A workout started on
 * another device is fetched once and then edited here.
 */
export function WorkoutPage({ id }: { id: string }) {
  const user = useUser();
  // null = not on this device.
  const local = useLiveQuery(() => db.workouts.get(id).then((w) => w ?? null), [id]);
  const [remote, setRemote] = useState<WorkoutDetail | null>(null);
  // The server's PR verdicts for this workout (used once it's finished; live ones are worked out here).
  const [serverPrs, setServerPrs] = useState<WorkoutDetail['prs'] | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Fetch the server's copy: to pick up edits from another device, or to show it if it isn't here.
  useEffect(() => {
    let alive = true;
    api(`/api/workouts/${id}`, WorkoutDetail)
      .then(async (d) => {
        if (!alive) return;
        setServerPrs(d.prs);
        // Yours, or (for the planner) anyone's: kept on this device to edit. Otherwise just shown.
        if (d.userId === user.id || isPlanner(user)) await mergeFromServer(d);
        else setRemote(d);
      })
      .catch((e: unknown) => alive && setError(isOffline(e) ? 'This workout isn’t saved on this device. It’ll show once you’re online.' : errorText(e)));
    return () => {
      alive = false;
    };
  }, [id, user]);

  if (local && (local.userId === user.id || isPlanner(user))) return local.deletedAt ? <Gone /> : <EditableWorkout user={user} workout={local} serverPrs={serverPrs} />;
  if (remote) return <ReadOnlyWorkout detail={remote} />;
  if (error) return <Gone message={error} />;
  if (local === null) return <p className="muted">Loading…</p>;
  return null;
}

function Gone({ message = 'This workout was deleted.' }: { message?: string }) {
  return (
    <div className="stack">
      <Link href="/" className="small">
        ‹ Today
      </Link>
      <p>{message}</p>
    </div>
  );
}

function useExercises(user: User) {
  const { data } = useCachedApi(user.id, '/api/exercises', ExerciseList);
  return useMemo(() => new Map((data?.exercises ?? []).map((e) => [e.id, e])), [data]);
}

/** Each person's saved pack (recent numbers and records), so their sets prefill and PRs show offline. */
function usePacks(user: User, ids: string[]) {
  const [packs, setPacks] = useState(new Map<string, LogPack>());
  const key = ids.join();
  useEffect(() => {
    let alive = true;
    for (const id of ids)
      cachedApi(user.id, packPath(id, user), LogPack)
        .then(({ data }) => alive && setPacks((p) => new Map(p).set(id, data)))
        .catch(() => {});
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.id, key]);
  return packs;
}

/** One person in a workout: whose it is, their workout record and sets, and what they're aiming for. */
type Member = {
  owner: store.Owner & { name: string };
  workout: LocalWorkout;
  sets: LocalSet[];
  history: Map<string, ExerciseHistory>;
  planned: Map<string, PlannedExercise>;
};

/**
 * A workout you can edit: your own, or (for the planner) a session done together, where every person has their
 * own workout with the same exercises and each exercise card logs everyone's sets.
 */
function EditableWorkout({ user, workout, serverPrs }: { user: User; workout: LocalWorkout; serverPrs: WorkoutDetail['prs'] | null }) {
  const [, navigate] = useLocation();
  const exercises = useExercises(user);
  const people = usePeople(user);
  const { data: splits } = useCachedApi(user.id, '/api/splits', SplitList);
  const canEdit = (w: { userId: string }) => w.userId === user.id || isPlanner(user);
  // Everyone's workouts in this session (just this one if it isn't shared), you first.
  const session =
    useLiveQuery(
      async () => (workout.sessionId ? (await db.workouts.where('sessionId').equals(workout.sessionId).toArray()).filter((w) => !w.deletedAt && canEdit(w)) : [workout]),
      [workout],
    ) ?? [workout];
  const nameOf = (id: string) => (id === user.id ? user.displayName : (people.find((p) => p.id === id)?.displayName ?? 'Someone'));
  const ordered = [...session].sort((a, b) => (a.userId === user.id ? -1 : b.userId === user.id ? 1 : nameOf(a.userId).localeCompare(nameOf(b.userId))));
  const ids = ordered.map((w) => w.id);
  const sets = useLiveQuery(() => db.sets.where('workoutId').anyOf(ids).toArray(), [ids.join()]) ?? [];
  const packs = usePacks(
    user,
    ordered.map((w) => w.userId),
  );
  const plannedDay = usePlannedDays(user, workout.date);
  const members: Member[] = ordered.map((w) => ({
    owner: { id: w.userId, units: w.userId === user.id ? user.units : (people.find((p) => p.id === w.userId)?.units ?? user.units), name: nameOf(w.userId) },
    workout: w,
    sets: sets.filter((s) => s.workoutId === w.id && !s.deletedAt).sort((a, b) => a.loggedAt - b.loggedAt),
    history: new Map((packs.get(w.userId)?.history ?? []).map((h) => [h.exerciseId, h])),
    planned: new Map((plannedDay(w.userId)?.exercises ?? []).map((p) => [p.exerciseId, p])),
  }));
  const ownerOf = (userId: string) => members.find((m) => m.owner.id === userId)?.owner ?? { id: userId, units: user.units, name: nameOf(userId) };

  // The server's PR verdicts for everyone else's workouts in the session (this one's come from the page).
  const [partnerPrs, setPartnerPrs] = useState<WorkoutDetail['prs']>({});
  const partnerIds = ids.filter((id) => id !== workout.id).join();
  useEffect(() => {
    let alive = true;
    for (const id of partnerIds ? partnerIds.split(',') : [])
      api(`/api/workouts/${id}`, WorkoutDetail)
        .then(async (d) => {
          await mergeFromServer(d);
          if (alive) setPartnerPrs((p) => ({ ...p, ...d.prs }));
        })
        .catch(() => {});
    return () => {
      alive = false;
    };
  }, [partnerIds]);

  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<LocalSet | null>(null);
  // "New PR: …", shown for a few seconds after a record-breaking set.
  const [prNote, setPrNote] = useState<{ text: string; at: number } | null>(null);
  useEffect(() => {
    if (!prNote) return;
    const t = setTimeout(() => setPrNote(null), 4_000);
    return () => clearTimeout(t);
  }, [prNote]);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const finished = workout.endedAt !== null;
  const together = members.length > 1;
  const allSets = members.flatMap((m) => m.sets);
  // Every exercise anyone has listed or logged, in the order this workout lists them.
  const exerciseIds = [...new Set([...workout.exerciseIds, ...members.flatMap((m) => [...m.workout.exerciseIds, ...m.sets.map((s) => s.exerciseId)])])];
  // People in the group who aren't in this workout (the planner can add them).
  const missing = isPlanner(user) && !finished ? people.filter((p) => !members.some((m) => m.owner.id === p.id)) : [];

  // Open the exercise picker straight away for a brand-new workout.
  useEffect(() => {
    if (!finished && workout.exerciseIds.length === 0 && Date.now() - workout.startedAt < 5_000) setAdding(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const split = splits?.splits.find((s) => s.id === workout.splitId);
  const groups: readonly MuscleGroup[] = split?.muscleGroups ?? [];
  const forAll = (f: (m: Member) => Promise<void>) => Promise.all(members.map(f));

  return (
    <div className="stack-lg workout">
      <div className="stack-sm">
        <Link href="/" className="small">
          ‹ Today
        </Link>
        <h1>{workout.name}</h1>
        <p className="muted">
          {formatDayLong(workout.date)} · <Elapsed from={workout.startedAt} to={workout.endedAt} />
        </p>
        {together ? (
          <p className="muted">Logging for {andList(members.map((m) => m.owner.name))}</p>
        ) : (
          workout.userId !== user.id && <p className="muted">{nameOf(workout.userId)}’s workout</p>
        )}
      </div>

      {exerciseIds.map((exId) => (
        <ExerciseCard
          key={exId}
          exerciseId={exId}
          exercise={exercises.get(exId)}
          members={members}
          finished={finished}
          serverPrs={finished ? { ...partnerPrs, ...serverPrs } : null}
          onRemove={() => forAll((m) => store.removeExercise(m.owner, m.workout.id, exId))}
          onLogged={(m, prs) => {
            if (!prs.length) return;
            navigator.vibrate?.(150);
            setPrNote({ text: `${together ? `${m.owner.name}: ` : ''}New PR: ${prs.map((k) => PR_NAMES[k]).join(', ')}`, at: Date.now() });
          }}
          onEdit={setEditing}
        />
      ))}

      {exerciseIds.length === 0 && <p className="muted center">No exercises yet.</p>}

      {!finished && (
        <button type="button" className="btn block" onClick={() => setAdding(true)}>
          + Add exercise
        </button>
      )}

      <Notes owner={ownerOf(workout.userId)} workout={workout} />

      {finished ? (
        <section className="card stack">
          <p>
            Finished at {timeFormat.format(workout.endedAt!)} · {allSets.length} {allSets.length === 1 ? 'set' : 'sets'}
          </p>
          <button type="button" className="btn" onClick={() => void forAll((m) => store.reopenWorkout(m.owner, m.workout.id))}>
            Reopen to add more
          </button>
        </section>
      ) : (
        <button type="button" className="btn block primary" onClick={() => void forAll((m) => store.finishWorkout(m.owner, m.workout.id))}>
          Finish workout
        </button>
      )}

      {!finished && (together || missing.length > 0) && (
        <div className="row wrap">
          {members
            .filter((m) => m.owner.id !== workout.userId && m.sets.length === 0)
            .map((m) => (
              <button key={m.owner.id} type="button" className="btn small" onClick={() => void store.deleteWorkout(m.owner, m.workout.id)}>
                {m.owner.name} isn’t here today
              </button>
            ))}
          {missing.map((p) => (
            <button key={p.id} type="button" className="btn small" onClick={() => void store.joinSession(p, workout)}>
              + Add {p.displayName}
            </button>
          ))}
        </div>
      )}

      <button type="button" className="btn small danger" onClick={() => setConfirmDelete(true)}>
        Delete workout
      </button>

      {prNote && (
        <p key={prNote.at} className="pr-toast" role="status">
          {prNote.text}
        </p>
      )}

      <Sheet open={adding} onClose={() => setAdding(false)} title="Add exercise">
        <ExercisePicker
          exercises={[...exercises.values()]}
          groups={groups}
          history={packs.get(workout.userId)?.history ?? []}
          today={todayIn(user.timezone)}
          exclude={exerciseIds}
          onPick={async (exId) => {
            await forAll((m) => store.addExercise(m.owner, m.workout.id, exId));
            setAdding(false);
          }}
        />
      </Sheet>

      <Sheet open={editing !== null} onClose={() => setEditing(null)} title={editing && together ? `Edit ${ownerOf(editing.userId).name}’s set` : 'Edit set'}>
        {editing && (
          <div className="stack">
            <SetEntry
              type={exercises.get(editing.exerciseId)?.type ?? 'weight_reps'}
              units={ownerOf(editing.userId).units}
              initial={prefill(exercises.get(editing.exerciseId)?.type ?? 'weight_reps', [editing], [], ownerOf(editing.userId).units)}
              initialExtras={{ warmup: editing.warmup }}
              submitLabel="Save"
              onSubmit={async (values, extras) => {
                await store.updateSet(ownerOf(editing.userId), editing.id, { ...values, ...extras });
                setEditing(null);
              }}
            />
            <button
              type="button"
              className="btn danger"
              onClick={async () => {
                await store.deleteSet(ownerOf(editing.userId), editing.id);
                setEditing(null);
              }}
            >
              Delete set
            </button>
          </div>
        )}
      </Sheet>

      <Sheet open={confirmDelete} onClose={() => setConfirmDelete(false)} title="Delete workout?">
        <p>
          This removes “{workout.name}”{together ? ` for ${andList(members.map((m) => m.owner.name))}` : ''} and {together ? 'their' : 'its'} {allSets.length}{' '}
          {allSets.length === 1 ? 'set' : 'sets'}. It can’t be undone.
        </p>
        <div className="row">
          <button type="button" className="btn grow" onClick={() => setConfirmDelete(false)}>
            Keep it
          </button>
          <button
            type="button"
            className="btn grow danger"
            onClick={async () => {
              await forAll((m) => store.deleteWorkout(m.owner, m.workout.id));
              navigate('/');
            }}
          >
            Delete
          </button>
        </div>
      </Sheet>
    </div>
  );
}

const andList = (names: string[]) => (names.length > 1 ? `${names.slice(0, -1).join(', ')} and ${names.at(-1)}` : (names[0] ?? ''));

/**
 * One exercise: each person's sets, and one entry box. Done together, a switch picks whose set is next, and it
 * flips to the next person after each set (everyone does a set, then round again).
 */
function ExerciseCard({
  exerciseId,
  exercise,
  members,
  finished,
  serverPrs,
  onRemove,
  onLogged,
  onEdit,
}: {
  exerciseId: string;
  exercise: Exercise | undefined;
  members: Member[];
  /** A finished workout's sets can still be corrected, but new ones need it reopened. */
  finished: boolean;
  serverPrs: WorkoutDetail['prs'] | null;
  onRemove: () => void;
  onLogged: (m: Member, prs: PrKind[]) => void;
  onEdit: (s: LocalSet) => void;
}) {
  const type = exercise?.type ?? 'weight_reps';
  const [activeId, setActiveId] = useState(members[0]?.owner.id);
  const active = members.find((m) => m.owner.id === activeId) ?? members[0];
  const together = members.length > 1;
  const setsOf = (m: Member) => m.sets.filter((s) => s.exerciseId === exerciseId);
  // Records before this workout (saved for offline use), then each set judged against the ones before it.
  const prsOf = (m: Member) => (serverPrs ? new Map(Object.entries(serverPrs)) : markPrs(type, setsOf(m), m.history.get(exerciseId)?.bests ?? EMPTY_BESTS));
  const nobodyLogged = members.every((m) => setsOf(m).length === 0);
  if (!active) return null;

  const mine = setsOf(active);
  const history = active.history.get(exerciseId);
  const planned = active.planned.get(exerciseId);
  const units = active.owner.units;
  const before = history?.bests ?? EMPTY_BESTS;
  const start = prefill(type, mine, history?.lastSets ?? [], units);
  // With a plan, the first set starts from the planned numbers.
  const initial =
    planned && mine.length === 0
      ? { weight: planned.weight ?? start.weight, reps: planned.reps ?? start.reps, durationS: planned.durationS ?? start.durationS, distanceM: planned.distanceM ?? start.distanceM }
      : start;
  const next = mine.filter((s) => !s.warmup).length + 1;

  const setList = (m: Member) => {
    const list = setsOf(m);
    const prs = prsOf(m);
    return (
      list.length > 0 && (
        <ol className="sets">
          {list.map((s, i) => (
            <li key={s.id}>
              <button type="button" className="set-row" onClick={() => onEdit(s)}>
                <span className="set-num">{s.warmup ? 'W' : i + 1 - list.slice(0, i).filter((x) => x.warmup).length}</span>
                <span className="grow">{describeSet(type, s, m.owner.units)}</span>
                <PrBadge kinds={prs.get(s.id)} />
                {s.dirty === 1 && (
                  <span className="pending-dot" title="Waiting to upload" aria-label="Waiting to upload">
                    •
                  </span>
                )}
              </button>
            </li>
          ))}
        </ol>
      )
    );
  };

  return (
    <section className="card stack exercise-card" aria-label={exercise?.name ?? 'Exercise'}>
      <div className="row">
        <h2 className="grow">{exercise ? <Link href={`/exercises/${exerciseId}`}>{exercise.name}</Link> : 'Exercise'}</h2>
        {nobodyLogged && !finished && (
          <button type="button" className="btn small" onClick={onRemove} aria-label={`Remove ${exercise?.name ?? 'exercise'}`}>
            Remove
          </button>
        )}
      </div>
      {together ? (
        members.map((m) => {
          const p = m.planned.get(exerciseId);
          return (
            <div key={m.owner.id} className={`member-sets${m === active && !finished ? ' on' : ''}`}>
              <p className="row">
                <strong className="grow">{m.owner.name}</strong>
                {p && <span className="muted small">Plan {planText(p, type, m.owner.units)} · {planProgress(p, type, setsOf(m), m.owner.units).done} of {p.sets}</span>}
              </p>
              {setList(m) || <p className="muted small">No sets yet.</p>}
            </div>
          );
        })
      ) : (
        <>
          {planned && !finished && <PlanLine planned={planned} type={type} sets={mine} units={units} />}
          {history && (
            <p className="muted small">
              Last time ({formatMonthDay(history.lastDate)}): {history.lastSets.map((s) => describeSet(type, s, units)).join(', ')}
            </p>
          )}
          {setList(active)}
        </>
      )}
      {exercise && !finished && (
        <>
          {together && (
            <>
              <span className="chips" role="group" aria-label="Whose set">
                {members.map((m) => (
                  <button key={m.owner.id} type="button" className={`chip toggle${m === active ? ' on' : ''}`} aria-pressed={m === active} onClick={() => setActiveId(m.owner.id)}>
                    {m.owner.name}
                  </button>
                ))}
              </span>
              {planned && <PlanLine planned={planned} type={type} sets={mine} units={units} />}
              {history && (
                <p className="muted small">
                  {active.owner.name} last time ({formatMonthDay(history.lastDate)}): {history.lastSets.map((s) => describeSet(type, s, units)).join(', ')}
                </p>
              )}
            </>
          )}
          <SetEntry
            // Re-seed the inputs for each person and after each set, and when the day's plan arrives (it can load after the card).
            key={`${active.owner.id}|${mine.length}|${planned ? `${planned.weight}x${planned.reps ?? planned.durationS}` : ''}`}
            type={type}
            units={units}
            initial={initial}
            submitLabel={type === 'cardio' ? (together ? `Log ${active.owner.name}’s` : 'Log') : together ? `Log ${active.owner.name}’s set ${next}` : `Log set ${next}`}
            onSubmit={async (values, extras) => {
              const set = await store.logSet(active.owner, active.workout.id, exerciseId, values, extras);
              onLogged(active, prsFor(type, set, bestsOf(type, mine, before)));
              // Next person's turn.
              if (together) setActiveId(members[(members.indexOf(active) + 1) % members.length]!.owner.id);
            }}
          />
        </>
      )}
    </section>
  );
}

function Notes({ owner, workout }: { owner: store.Owner; workout: WorkoutRecord }) {
  const [text, setText] = useState(workout.notes ?? '');
  // Pick up notes changed elsewhere (another device) when not mid-edit.
  const [focused, setFocused] = useState(false);
  useEffect(() => {
    if (!focused) setText(workout.notes ?? '');
  }, [workout.notes, focused]);
  return (
    <section className="card">
      <label className="field">
        <span>Notes</span>
        <textarea
          rows={2}
          value={text}
          maxLength={4000}
          placeholder="How it went, what to change next time…"
          onFocus={() => setFocused(true)}
          onChange={(e) => setText(e.target.value)}
          onBlur={() => {
            setFocused(false);
            if ((text.trim() || null) !== workout.notes) void store.setNotes(owner, workout.id, text);
          }}
        />
      </label>
    </section>
  );
}

function Elapsed({ from, to }: { from: number; to: number | null }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (to !== null) return;
    const t = setInterval(() => setNow(Date.now()), 15_000);
    return () => clearInterval(t);
  }, [to]);
  const min = Math.max(0, Math.round(((to ?? now) - from) / 60_000));
  return <>{to === null ? `started ${timeFormat.format(from)} · ${min} min` : `${min} min`}</>;
}

/** Someone else's workout, as the server has it. */
function ReadOnlyWorkout({ detail }: { detail: WorkoutDetail }) {
  const user = useUser();
  const exercises = useExercises(user);
  const { workout, sets } = detail;
  const ids = [...new Set([...workout.exerciseIds, ...sets.map((s) => s.exerciseId)])];
  return (
    <div className="stack-lg workout">
      <div className="stack-sm">
        <Link href="/" className="small">
          ‹ Today
        </Link>
        <h1>{workout.name}</h1>
        <p className="muted">{detail.userName}’s workout</p>
        <p className="muted">
          {formatDayLong(workout.date)} · <Elapsed from={workout.startedAt} to={workout.endedAt} />
        </p>
      </div>
      {ids.map((exId) => {
        const exercise = exercises.get(exId);
        const mine: SetRecord[] = sets.filter((s) => s.exerciseId === exId);
        return (
          <section key={exId} className="card stack" aria-label={exercise?.name ?? 'Exercise'}>
            <h2>{exercise ? <Link href={`/exercises/${exId}`}>{exercise.name}</Link> : 'Exercise'}</h2>
            {mine.length ? (
              <ol className="sets">
                {mine.map((s, i) => (
                  <li key={s.id} className="set-row static">
                    <span className="set-num">{s.warmup ? 'W' : i + 1 - mine.slice(0, i).filter((x) => x.warmup).length}</span>
                    <span className="grow">{describeSet(exercise?.type ?? 'weight_reps', s, user.units)}</span>
                    <PrBadge kinds={detail.prs[s.id]} />
                  </li>
                ))}
              </ol>
            ) : (
              <p className="muted small">No sets yet.</p>
            )}
          </section>
        );
      })}
      {workout.notes && <p className="notes card">{workout.notes}</p>}
    </div>
  );
}
